import React from 'react';
import type { Locale } from '@/lib/i18n';

/**
 * Inline SVG flag icons.
 *
 * We render real SVGs instead of emoji flags because Windows does not ship
 * with a colour emoji font that supports regional indicator pairs, which
 * means flag emoji collapse to letter-pairs (e.g. "NL", "GB") on Windows.
 * Inline SVGs render identically on every operating system and browser.
 */

type FlagIconProps = {
  locale: Locale;
  className?: string;
  title?: string;
};

const TITLES: Record<Locale, string> = {
  en: 'United Kingdom',
  nl: 'Nederland',
  de: 'Deutschland',
  sv: 'Sverige',
};

function GBFlag({ title }: { title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 60 30"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={title}
    >
      <clipPath id="flag-gb-s">
        <path d="M0,0 v30 h60 v-30 z" />
      </clipPath>
      <clipPath id="flag-gb-t">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath="url(#flag-gb-s)">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path
          d="M0,0 L60,30 M60,0 L0,30"
          clipPath="url(#flag-gb-t)"
          stroke="#C8102E"
          strokeWidth="4"
        />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function NLFlag({ title }: { title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 9 6"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={title}
    >
      <rect width="9" height="6" fill="#fff" />
      <rect width="9" height="2" fill="#AE1C28" />
      <rect y="4" width="9" height="2" fill="#21468B" />
    </svg>
  );
}

function DEFlag({ title }: { title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 5 3"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={title}
    >
      <rect width="5" height="1" y="0" fill="#000" />
      <rect width="5" height="1" y="1" fill="#DD0000" />
      <rect width="5" height="1" y="2" fill="#FFCE00" />
    </svg>
  );
}

/**
 * Swedish flag.
 *
 * Uses the Flag of Sweden Act (1982:269) construction sheet:
 * proportions 5+2+9 horizontally and 4+2+4 vertically on a 16×10 grid,
 * giving the canonical aspect ratio 8:5.
 * Colours per PMS spec: blue PMS 301 C (#006AA7), yellow PMS 116 C (#FECC00).
 */
function SEFlag({ title }: { title: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 10"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={title}
    >
      <rect width="16" height="10" fill="#006AA7" />
      <rect x="5" width="2" height="10" fill="#FECC00" />
      <rect y="4" width="16" height="2" fill="#FECC00" />
    </svg>
  );
}

export function FlagIcon({ locale, className, title }: FlagIconProps) {
  const label = title || TITLES[locale] || locale.toUpperCase();
  let flag: React.ReactNode;
  switch (locale) {
    case 'en':
      flag = <GBFlag title={label} />;
      break;
    case 'nl':
      flag = <NLFlag title={label} />;
      break;
    case 'de':
      flag = <DEFlag title={label} />;
      break;
    case 'sv':
      flag = <SEFlag title={label} />;
      break;
    default:
      flag = null;
  }

  return <span className={className}>{flag}</span>;
}

export default FlagIcon;
